import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Primary, SemanticSuccess } from '../../../../core-components/theme/color-palette/colors';
import { InlineSpinner } from '../../../../core-components/theme/components/Buttons/InlineSpinner';
import { SettingsRow } from '../../../../core-components/theme/components/SettingsList';
import type { HandoffStall } from '../../types/domain.types';

export type HandoffSaveStatus = 'idle' | 'saving' | 'saved' | 'failed';

export interface HandoffToggleProps {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (next: boolean) => void;
  readonly stall: HandoffStall | null;
  readonly status?: HandoffSaveStatus;
  readonly changed?: boolean;
}

const label = (t: TFunction): string => t('closeTheLoop.settings.handoffAlerts', 'Too many hand-offs');
const rule = (t: TFunction, stall: HandoffStall | null): string => {
  if (stall === null) {
    return t('closeTheLoop.settings.handoffAlertsRuleGeneric', 'When one Issue keeps being handed on and nobody starts it');
  }
  return t('closeTheLoop.settings.handoffAlertsRule', '{{count}} hand-offs in {{hours}} hours on one Issue', {
    count: stall.handoffs,
    hours: stall.withinHours,
  });
};

const statusSx = { display: 'flex', alignItems: 'center', gap: 1 } as const;
const savedSx = { ...statusSx, color: SemanticSuccess[900] } as const;
const changedSx = { display: 'block', color: Primary[600] } as const;

const StatusContent: React.FC<{ status: HandoffSaveStatus }> = ({ status }) => {
  const { t } = useTranslation();
  if (status === 'saving') {
    return (
      <>
        <InlineSpinner aria-hidden />
        {t('coreComponents.saveBar.saving', 'Saving…')}
      </>
    );
  }
  if (status !== 'saved') return null;
  return (
    <>
      <CheckCircleOutlineIcon fontSize="small" aria-hidden />
      {t('closeTheLoop.settings.handoffSaved', 'Saved')}
    </>
  );
};

const SaveStatus: React.FC<{ status: HandoffSaveStatus | undefined }> = ({ status }) => {
  if (status === undefined) return null;
  return (
    <Box component="span" role="status" sx={status === 'saved' ? savedSx : statusSx}>
      <StatusContent status={status} />
    </Box>
  );
};

const ChangedMark: React.FC<{ changed: boolean }> = ({ changed }) => {
  const { t } = useTranslation();
  if (!changed) return null;
  return (
    <Typography component="span" variant="caption2" sx={changedSx}>
      {t('closeTheLoop.settings.changed', 'Changed')}
    </Typography>
  );
};

const descriptionOf = (ruleText: string, status: HandoffSaveStatus | undefined, changed: boolean): React.ReactNode => {
  if (status === undefined && !changed) return ruleText;
  return (
    <>
      {ruleText}
      <ChangedMark changed={changed} />
      <SaveStatus status={status} />
    </>
  );
};

export const HandoffToggle: React.FC<HandoffToggleProps> = ({ checked, disabled, onChange, stall, status, changed = false }) => {
  const { t } = useTranslation();
  return (
    <SettingsRow
      control="switch"
      label={label(t)}
      description={descriptionOf(rule(t, stall), status, changed)}
      checked={checked}
      disabled={disabled}
      busy={status === 'saving'}
      onCheckedChange={onChange}
    />
  );
};
